import { format } from 'date-fns';
import { CalendarClock, Pencil, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { zonedEvent } from '@/lib/timezone';

import type { MemoryListItem } from '../api/use-memories';

/** "Everywhere" for an unscoped memory, or the joined skill labels — kept
 *  local rather than importing the features package's
 *  `scopeLabelForSkillIds` (frontend consumes the API, not features
 *  directly; see `.claude/rules/nextjs.md`). The scope-id → label mapping
 *  itself is fetched from `GET /assistant/memory-scopes`
 *  (`useMemoryScopes`), so this function only needs to join what it's given. */
const formatScopeLabel = (
  scopeSkillIds: string[] | null,
  labelsById: Record<string, string>
): string => {
  if (!scopeSkillIds || scopeSkillIds.length === 0) return 'Everywhere';
  return scopeSkillIds.map((id) => labelsById[id] ?? id).join(', ');
};

export interface MemoryRowProps {
  memory: MemoryListItem;
  onEdit: (memory: MemoryListItem) => void;
  onDelete: (memory: MemoryListItem) => void;
  onToggleEnabled: (memory: MemoryListItem, enabled: boolean) => void;
  /** Skill id → display label, from `useMemoryScopes`. */
  scopeLabelsById: Record<string, string>;
  /** The BUSINESS timezone — an expiry is 23:59 in it, so rendering in the
   *  viewer's zone would name the next day for anyone east of the clinic. */
  timeZone: string;
  isBusy?: boolean;
}

const formatDate = (iso: string, timeZone: string): string => {
  if (Number.isNaN(new Date(iso).getTime())) return '';
  return format(zonedEvent(iso, timeZone), 'd MMM yyyy');
};

/**
 * One row in the memories list. Renders the memory's content with an
 * ownership badge (Personal / Team), a skill-scope badge (Phase 3,
 * docs/plans/claire-memory-suggestions.md — "Everywhere" for an unscoped
 * memory), an expiry chip when the memory is time-bound, an inline
 * enabled/disabled toggle, and edit/delete buttons.
 *
 * Memories with an expiry are dropped by the API once they lapse, so a
 * chip shown here always names a future date — it reads as "Claire uses
 * this until…", never as a tombstone. A DISABLED memory, by contrast, stays
 * in this list (dimmed, with a "Disabled" badge) — the whole point of
 * `enabled` is opt-out-without-delete, so the owner needs to see it here to
 * re-enable it.
 */
export function MemoryRow({
  memory,
  onEdit,
  onDelete,
  onToggleEnabled,
  scopeLabelsById,
  timeZone,
  isBusy = false,
}: MemoryRowProps) {
  const isPersonal = memory.scope === 'personal';
  const expiry = memory.expiresAt ? formatDate(memory.expiresAt, timeZone) : '';
  const scopeLabel = formatScopeLabel(memory.scopeSkillIds, scopeLabelsById);

  return (
    <Card className={memory.enabled ? undefined : 'opacity-60'}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={isPersonal ? 'secondary' : 'outline'}>
              {isPersonal ? 'Personal' : 'Team'}
            </Badge>
            <Badge variant="outline" className="font-normal">
              {scopeLabel}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {formatDate(memory.createdAt, timeZone)}
            </span>
            {expiry && (
              <Badge className="gap-1 font-normal" variant="secondary">
                <CalendarClock className="size-3" />
                Until {expiry}
              </Badge>
            )}
            {!memory.enabled && (
              <Badge variant="destructive" className="font-normal">
                Disabled
              </Badge>
            )}
          </div>
          <p className="whitespace-pre-wrap break-words text-sm">
            {memory.content}
          </p>
        </div>

        {memory.canManage ? (
          <div className="flex shrink-0 items-center gap-2">
            <Switch
              checked={memory.enabled}
              onCheckedChange={(checked) => onToggleEnabled(memory, checked)}
              disabled={isBusy}
              aria-label={memory.enabled ? 'Disable memory' : 'Enable memory'}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(memory)}
              disabled={isBusy}
              aria-label="Edit memory"
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(memory)}
              disabled={isBusy}
              aria-label="Delete memory"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ) : (
          <p className="shrink-0 text-muted-foreground text-xs">
            Only admins can change team memories
          </p>
        )}
      </CardContent>
    </Card>
  );
}
